import { personIcon } from './icons';

const ROOT_CLASS = 'flix-chat-widget__customer-info';

// A presentation-only data shape — deliberately not tied to Shopify, a CRM,
// or any specific backend. Every field is optional and every renderer below
// skips (never shows a fake placeholder for) whatever isn't supplied, so
// this same component keeps working whether it's fed nothing, a partial
// record, or eventually a real integration's full response. See the
// "Customer Info Panel" note in the design integration report: this ships
// as UI only in this pass — nothing in the widget calls a provider by
// default (CreateWidgetOptions.getCustomerInfo is undefined unless a caller
// explicitly wires one up), so no placeholder/mock data ever reaches a real
// customer.
export interface CustomerInfoStat {
  label: string;
  value: string;
}

export interface CustomerInfoRow {
  label: string;
  value: string;
}

export interface CustomerInfoOrder {
  reference: string;
  status: string;
  description: string;
  amount: string;
  placedAt: string;
}

export interface CustomerInfo {
  name?: string;
  email?: string;
  avatarInitial?: string;
  isActive?: boolean;
  stats?: CustomerInfoStat[];
  profileRows?: CustomerInfoRow[];
  latestOrder?: CustomerInfoOrder;
  conversationRows?: CustomerInfoRow[];
}

// Resolves a CustomerInfo for the given conversation, from whatever backend
// a future integration wires up. Not called anywhere by default.
export type CustomerInfoProvider = (conversationId: string) => Promise<CustomerInfo | null>;

export interface CustomerInfoPanel {
  element: HTMLElement;
  showLoading(): void;
  showUnavailable(): void;
  render(info: CustomerInfo): void;
}

function row(label: string, value: string): HTMLElement {
  const element = document.createElement('div');
  element.className = `${ROOT_CLASS}-row`;

  const labelEl = document.createElement('span');
  labelEl.className = `${ROOT_CLASS}-row-label`;
  labelEl.textContent = label;

  const valueEl = document.createElement('span');
  valueEl.className = `${ROOT_CLASS}-row-value`;
  valueEl.textContent = value;

  element.append(labelEl, valueEl);
  return element;
}

export function createCustomerInfoPanel(onBack: () => void): CustomerInfoPanel {
  const element = document.createElement('div');
  element.className = ROOT_CLASS;

  const header = document.createElement('div');
  header.className = `${ROOT_CLASS}-header`;

  const backButton = document.createElement('button');
  backButton.type = 'button';
  backButton.className = `${ROOT_CLASS}-back`;
  backButton.setAttribute('aria-label', 'Back to conversation');
  backButton.textContent = '←';
  backButton.addEventListener('click', () => onBack());

  const title = document.createElement('div');
  title.className = `${ROOT_CLASS}-header-title`;
  title.textContent = 'Customer details';

  header.append(backButton, title);

  const body = document.createElement('div');
  body.className = `${ROOT_CLASS}-body`;

  element.append(header, body);

  function clear(): void {
    body.replaceChildren();
  }

  return {
    element,

    showLoading(): void {
      clear();
      const loading = document.createElement('div');
      loading.className = `${ROOT_CLASS}-placeholder`;
      loading.textContent = 'Loading…';
      body.append(loading);
    },

    showUnavailable(): void {
      clear();
      const unavailable = document.createElement('div');
      unavailable.className = `${ROOT_CLASS}-placeholder`;
      unavailable.textContent = 'Customer details are not available.';
      body.append(unavailable);
    },

    render(info: CustomerInfo): void {
      clear();

      const profile = document.createElement('div');
      profile.className = `${ROOT_CLASS}-profile`;

      const avatar = document.createElement('div');
      avatar.className = `${ROOT_CLASS}-avatar`;
      if (info.avatarInitial) {
        avatar.textContent = info.avatarInitial;
      } else {
        avatar.append(personIcon());
      }

      profile.append(avatar);

      if (info.name) {
        const name = document.createElement('div');
        name.className = `${ROOT_CLASS}-name`;
        name.textContent = info.name;
        profile.append(name);
      }

      if (info.email) {
        const email = document.createElement('div');
        email.className = `${ROOT_CLASS}-email`;
        email.textContent = info.email;
        profile.append(email);
      }

      if (info.isActive) {
        const badge = document.createElement('div');
        badge.className = `${ROOT_CLASS}-active-badge`;
        badge.textContent = 'Active conversation';
        profile.append(badge);
      }

      body.append(profile);

      if (info.stats && info.stats.length > 0) {
        const statsGrid = document.createElement('div');
        statsGrid.className = `${ROOT_CLASS}-stats`;

        for (const stat of info.stats) {
          const cell = document.createElement('div');
          cell.className = `${ROOT_CLASS}-stat`;

          const value = document.createElement('div');
          value.className = `${ROOT_CLASS}-stat-value`;
          value.textContent = stat.value;

          const label = document.createElement('div');
          label.className = `${ROOT_CLASS}-stat-label`;
          label.textContent = stat.label;

          cell.append(value, label);
          statsGrid.append(cell);
        }

        body.append(statsGrid);
      }

      if (info.profileRows && info.profileRows.length > 0) {
        const section = document.createElement('div');
        section.className = `${ROOT_CLASS}-section`;

        const heading = document.createElement('div');
        heading.className = `${ROOT_CLASS}-section-title`;
        heading.textContent = 'Profile';
        section.append(heading);

        for (const item of info.profileRows) {
          section.append(row(item.label, item.value));
        }

        body.append(section);
      }

      if (info.latestOrder) {
        const section = document.createElement('div');
        section.className = `${ROOT_CLASS}-section`;

        const heading = document.createElement('div');
        heading.className = `${ROOT_CLASS}-section-title`;
        heading.textContent = 'Latest order';
        section.append(heading);

        const card = document.createElement('div');
        card.className = `${ROOT_CLASS}-order-card`;

        const top = document.createElement('div');
        top.className = `${ROOT_CLASS}-order-top`;

        const reference = document.createElement('span');
        reference.textContent = info.latestOrder.reference;

        const status = document.createElement('span');
        status.className = `${ROOT_CLASS}-order-status`;
        status.textContent = info.latestOrder.status;

        top.append(reference, status);

        const description = document.createElement('div');
        description.className = `${ROOT_CLASS}-order-description`;
        description.textContent = info.latestOrder.description;

        const amount = document.createElement('span');
        amount.textContent = info.latestOrder.amount;
        description.append(amount);

        const placedAt = document.createElement('div');
        placedAt.className = `${ROOT_CLASS}-order-date`;
        placedAt.textContent = info.latestOrder.placedAt;

        card.append(top, description, placedAt);
        section.append(card);
        body.append(section);
      }

      if (info.conversationRows && info.conversationRows.length > 0) {
        const section = document.createElement('div');
        section.className = `${ROOT_CLASS}-section`;

        const heading = document.createElement('div');
        heading.className = `${ROOT_CLASS}-section-title`;
        heading.textContent = 'Conversation';
        section.append(heading);

        for (const item of info.conversationRows) {
          section.append(row(item.label, item.value));
        }

        body.append(section);
      }
    },
  };
}
